import { useState, useEffect } from 'react';
import { notificationsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Bell, Mail, MessageSquare, CheckCircle, Search, Trash2, AlertCircle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { BulkActionBar } from '@/components/BulkActions';

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (search.trim()) params.search = search.trim();
      const r = await notificationsAPI.list(params);
      setNotifications(r.data);
    } catch (e) { console.error('Notifications load error:', e); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const markSent = async (id) => {
    try {
      await notificationsAPI.update(id, { status: 'sent' });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, status: 'sent' } : n));
      toast.success('Marked as sent');
    } catch { toast.error('Update failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notification?')) return;
    try {
      await notificationsAPI.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Notification deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleDeleteAll = async () => {
    const label = filter === 'all' ? 'ALL' : filter;
    if (!window.confirm(`Delete ${label} notifications? This cannot be undone.`)) return;
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const r = await notificationsAPI.deleteAll(params);
      toast.success(r.data.message);
      load();
    } catch { toast.error('Delete failed'); }
  };

  const typeLabel = (type) => {
    const map = {
      'inventory_match': 'Inventory Match',
      'new_inventory': 'New Listing',
      'new_lead': 'New Lead',
      'deal_update': 'Deal Update',
      'portal_invite_sent': 'Portal Invite',
      'hold_deposit_sent': 'Hold Deposit',
      'listing_sent': 'Listing Sent',
      'document_sent': 'Document Sent',
    };
    return map[type] || (type || 'General').replace(/_/g, ' ');
  };

  const typeBadgeColor = (type) => {
    const map = {
      'inventory_match': 'bg-indigo-100 text-indigo-800 border-indigo-300',
      'new_inventory': 'bg-blue-100 text-blue-800 border-blue-300',
      'new_lead': 'bg-purple-100 text-purple-800 border-purple-300',
      'deal_update': 'bg-teal-100 text-teal-800 border-teal-300',
      'portal_invite_sent': 'bg-cyan-100 text-cyan-800 border-cyan-300',
      'hold_deposit_sent': 'bg-orange-100 text-orange-800 border-orange-300',
      'listing_sent': 'bg-sky-100 text-sky-800 border-sky-300',
      'document_sent': 'bg-emerald-100 text-emerald-800 border-emerald-300',
    };
    return map[type] || 'bg-gray-100 text-gray-700 border-gray-300';
  };

  const statusBadge = (status) => {
    if (status === 'sent') return 'bg-green-100 text-green-800 border-green-300';
    if (status === 'completed') return 'bg-green-100 text-green-800 border-green-300';
    if (status === 'failed') return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-amber-100 text-amber-800 border-amber-300';
  };

  const channelIcon = (ch) => {
    if (ch === 'email') return <Mail className="w-3 h-3" />;
    if (ch === 'sms') return <MessageSquare className="w-3 h-3" />;
    if (ch === 'whatsapp') return <MessageSquare className="w-3 h-3" />;
    return <Bell className="w-3 h-3" />;
  };

  return (
    <div data-testid="admin-notifications">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 uppercase">Notifications</h1>
          <p className="text-gray-500 text-sm mt-0.5">Automated alerts for clients & leads</p>
        </div>
        {notifications.length > 0 && (
          <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" onClick={handleDeleteAll} data-testid="delete-all-notifs">
            <Trash2 className="w-3 h-3 mr-1" /> Clear {filter !== 'all' ? filter : 'All'}
          </Button>
        )}
      </div>

      {/* Search + Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by client, email, message, unit..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()}
            className="pl-9 bg-white border-gray-200 text-gray-900"
            data-testid="notif-search"
          />
        </div>
        <Button variant="outline" className="border-gray-200 text-gray-600" onClick={load}>Search</Button>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-36 bg-white border-gray-200 text-gray-700" data-testid="notif-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Count */}
      <p className="text-xs text-gray-500 mb-2">
        {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
        {notifications.length > 0 && (
          <button className="ml-2 text-blue-500 hover:underline" onClick={() => setSelected(p => p.length === notifications.length ? [] : notifications.map(n => n.id))}>
            {selected.length === notifications.length ? 'Deselect All' : 'Select All'}
          </button>
        )}
      </p>

      {/* Notifications List */}
      <div className="space-y-2">
        {loading ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-400">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <Bell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No notifications found</p>
            <p className="text-gray-400 text-xs mt-1">Notifications are created when new inventory is added or matches client preferences.</p>
          </div>
        ) : notifications.map(n => (
          <div key={n.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:border-gray-300 transition-colors" data-testid={`notif-${n.id}`}>
            <div className="flex items-start gap-3">
              {/* Checkbox */}
              <Checkbox checked={selected.includes(n.id)} onCheckedChange={() => toggleSelect(n.id)} className="mt-1 border-gray-300" data-testid={`select-notif-${n.id}`} />
              {/* Icon */}
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0 mt-0.5">
                {n.type === 'new_inventory' ? <AlertCircle className="w-4 h-4 text-blue-600" /> :
                 n.type === 'inventory_match' ? <Bell className="w-4 h-4 text-indigo-600" /> :
                 <Mail className="w-4 h-4 text-gray-500" />}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <Badge className={`text-[11px] border ${typeBadgeColor(n.type)}`} data-testid={`notif-type-${n.id}`}>
                    {typeLabel(n.type)}
                  </Badge>
                  <Badge className={`text-[11px] border ${statusBadge(n.status)}`} data-testid={`notif-status-${n.id}`}>
                    {n.status === 'sent' ? 'Sent' : n.status === 'pending' ? 'Pending' : n.status}
                  </Badge>
                  {(n.channels || []).map(ch => (
                    <span key={ch} className="inline-flex items-center gap-0.5 text-[10px] text-gray-500">
                      {channelIcon(ch)} {ch}
                    </span>
                  ))}
                </div>

                {/* Title / recipient */}
                <p className="text-sm font-medium text-gray-900">
                  {n.title || n.unit_title || n.message?.slice(0, 60) || 'Notification'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {n.client_name || n.recipient_name || '--'}
                  {n.client_email ? ` (${n.client_email})` : n.sent_to ? ` (${n.sent_to})` : ''}
                  {n.client_phone ? ` | ${n.client_phone}` : ''}
                </p>

                {/* Message */}
                {n.message && (
                  <p className="text-xs text-gray-600 mt-1.5 bg-gray-50 rounded px-2 py-1.5 border border-gray-100 line-clamp-2">
                    {n.message}
                  </p>
                )}
              </div>

              {/* Date + Actions */}
              <div className="text-right shrink-0">
                <p className="text-[11px] text-gray-400">{new Date(n.created_at).toLocaleDateString()}</p>
                <p className="text-[10px] text-gray-300">{new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                <div className="flex items-center gap-1 mt-2 justify-end">
                  {n.status === 'pending' && (
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-green-700 hover:bg-green-50" onClick={() => markSent(n.id)} title="Mark as Sent" data-testid={`mark-sent-${n.id}`}>
                      <CheckCircle className="w-3 h-3" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-red-500 hover:bg-red-50" onClick={() => handleDelete(n.id)} title="Delete" data-testid={`delete-notif-${n.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Help text */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs text-gray-500">
        <strong className="text-gray-700">How it works:</strong> Notifications are automatically created when new inventory is added (alerts all clients & leads) or when you click "Match to Clients" in inventory management. Once Twilio/SendGrid keys are configured in Settings, messages will be sent automatically.
      </div>

      <BulkActionBar count={selected.length} onDelete={async () => {
        if (!window.confirm(`Delete ${selected.length} notification(s)?`)) return;
        try { await notificationsAPI.bulkDelete(selected); toast.success(`${selected.length} notification(s) deleted`); setSelected([]); load(); } catch { toast.error('Failed'); }
      }} onClear={() => setSelected([])} label="notification(s)" />
    </div>
  );
}
